import { useTranslation } from 'react-i18next';
import { useGetAdminSettings } from '../../../../queries';
import { AdminSettingsResponse } from '../../../types';
import {
  transformAdminSettingsToFormValues,
  transformAdminSettingsToRequestValues
} from '../../../utils';
import * as yup from 'yup';
import { SectionSkeleton } from 'components/shared/MultiStepForm/Skeletons/SectionSkeleton';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import FormContent from './components/FormContent';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { isSDBAccount } from 'utils/misc';
import Box from '@mui/material/Box';

const schema = yup.object({
  neg_hours_base: yup.number().required(),
  neg_hours_factor: yup.number().required(),
  neg_hours_min_time_block: yup.number().required()
});

const activityPlanningSchema = yup.object({
  activity_planning_masterdata_application: yup.string().required(),
  activity_planning_workforcemanagement_application: yup.string().required()
});

const createSchema = (isSDBAccount) =>
  isSDBAccount ? schema.concat(activityPlanningSchema) : schema;

const negativeAvailabilityGroup = 'negative-availability';
const activityPlanningGroup = 'activity-planning';

const ActivityPlanning = () => {
  const { t } = useTranslation('common');
  const {
    data: negativeAvailabilityData = [],
    isFetching: isNegativeAvailabilityDataFetching
  } = useGetAdminSettings<AdminSettingsResponse>(negativeAvailabilityGroup);

  const {
    data: activityPlanningData = [],
    isFetching: isActivityPlanningDataFetching
  } = useGetAdminSettings<AdminSettingsResponse>(activityPlanningGroup);

  const {
    mutateAsync: mutateNegativeAvailability,
    isLoading: isLoadingNegativeAvailability
  } = useSaveResource({
    url: `${ADMIN_SETTINGS_GROUP}`,
    id: negativeAvailabilityData[0]?.groupId,
    invalidateQueryKey: [negativeAvailabilityGroup],
    message: { error: t('negativeAvailabilitySettings.saveError') }
  });

  const {
    mutateAsync: mutateActivityPlanningGroup,
    isLoading: isLoadingActivityPlanningGroup
  } = useSaveResource({
    url: `${ADMIN_SETTINGS_GROUP}`,
    id: activityPlanningData[0]?.groupId,
    invalidateQueryKey: [activityPlanningGroup],
    message: { error: t('negativeAvailabilitySettings.saveError') }
  });

  const onSubmit = async (formData) => {
    const negativeAvailabilityPayload = {
      ...negativeAvailabilityData[0],
      settings: transformAdminSettingsToRequestValues(
        formData,
        negativeAvailabilityData[0]?.settings
      )
    };

    await mutateNegativeAvailability(negativeAvailabilityPayload);

    if (isSDBAccount()) {
      const activityPlanningPayload = {
        ...activityPlanningData[0],
        settings: transformAdminSettingsToRequestValues(
          formData,
          activityPlanningData[0]?.settings
        )
      };

      await mutateActivityPlanningGroup(activityPlanningPayload);
    }
  };

  return (
    <Box pt={'25px'}>
      {isNegativeAvailabilityDataFetching || isActivityPlanningDataFetching ? (
        <SectionSkeleton height={60} width={835} />
      ) : (
        <AdminSettingsFormWrapper
          submitForm={onSubmit}
          schema={createSchema(isSDBAccount())}
          isFetching={
            isNegativeAvailabilityDataFetching || isActivityPlanningDataFetching
          }
          isLoading={
            isLoadingNegativeAvailability || isLoadingActivityPlanningGroup
          }
          data={{
            ...transformAdminSettingsToFormValues(negativeAvailabilityData[0]),
            ...transformAdminSettingsToFormValues(activityPlanningData[0])
          }}
        >
          <FormContent />
        </AdminSettingsFormWrapper>
      )}
    </Box>
  );
};

export default ActivityPlanning;
